import {
  PlanForecastFragment,
  PlanItemFragment,
  PlanProductFragment,
  PlanShipmentFragment,
} from '@/core/api/graphql/forshawGraphqlApi.generated.ts'
import {
  ForecastItem,
  PlanItem,
  PlannedItem,
  Product,
  ShipmentItem,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { getPlanFormattedDateFromMonthAndYear } from '@/core/utils/getPlanFormattedDateFromMonthAndYear.ts'
import { getPlanFormattedDateFromDate } from '@/core/utils/getPlanFormattedDateFromDate.ts'

function mapReplenishmentPlanItemFragment(
  product: PlanProductFragment,
  forecasts: PlanForecastFragment[],
  planItems: PlanItemFragment[],
  shipments: PlanShipmentFragment[],
) {
  const forecastItems: PlanItem<ForecastItem> = {}
  const plannedItems: PlanItem<PlannedItem> = {}
  const shipmentItems: PlanItem<ShipmentItem[]> = {}

  const planProduct: Product = {
    id: product.id,
    name: product.name,
    code: product.code,
    unit: product.unit ?? null,
    photo: product.photo ?? null,
    price: parseFloat(product.price),
    caseQuantity: product.caseQuantity ?? null,
    palletQuantity: product.palletQuantity ?? null,
    erpCatalogName: product.erpCatalogName ?? '',
    category: product.category
      ? {
          id: product.category.id,
          name: product.category.name,
          code: product.category.code,
        }
      : null,
    group: product.group
      ? {
          id: product.group.id,
          name: product.group.name,
          code: product.group.code,
        }
      : null,
    speciesIds: product.speciesIds,
    isInPlan: false,
    unavailable: product.unavailable ?? false,
    salableProductId: null,
  }

  forecastItems[product.id] = {}

  for (const forecastItem of forecasts) {
    const productForecast = forecastItems[product.id]

    if (!productForecast) {
      continue
    }

    productForecast[
      getPlanFormattedDateFromMonthAndYear(
        forecastItem.month,
        forecastItem.year,
      )
    ] = {
      amountSuggested: forecastItem.amountSuggested,
      amountMaxSuggested: forecastItem.amountMaxSuggested,
      amountMinSuggested: forecastItem.amountMinSuggested,
    }
  }

  plannedItems[product.id] = {}

  for (const planItem of planItems) {
    if (planItem.amount == null) {
      continue
    }

    const productPlannedItems = plannedItems[product.id]

    if (!productPlannedItems) {
      continue
    }

    productPlannedItems[
      getPlanFormattedDateFromMonthAndYear(planItem.month, planItem.year)
    ] = {
      amount: planItem.amount,
    }
  }

  shipmentItems[product.id] = {}

  for (const shipmentItem of shipments) {
    if (!shipmentItem.product?.price) {
      continue
    }

    const productShipmentItems = shipmentItems[product.id]

    if (!productShipmentItems) {
      continue
    }

    if (
      !productShipmentItems[getPlanFormattedDateFromDate(shipmentItem.date)]
    ) {
      productShipmentItems[getPlanFormattedDateFromDate(shipmentItem.date)] = []
    }

    productShipmentItems[getPlanFormattedDateFromDate(shipmentItem.date)].push({
      amount: shipmentItem.amount,
      price: parseFloat(shipmentItem.product.price),
    })
  }

  return { planProduct, forecastItems, plannedItems, shipmentItems }
}

export { mapReplenishmentPlanItemFragment }
